import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  deletePacket,
  IntelPacket,
  PacketMapPolygon,
  listPackets,
  formatPacketMonitorLine,
  normalizePacketStatus,
  PACKET_STATUS_COLOR,
  PACKET_STATUS_LABELS,
} from '@signals/app-shell/services/intelPacketApi';
import {
  Mission,
  MissionRouteStatus,
  MissionSummary,
  MISSION_STATUS_COLOR,
  MISSION_STATUS_LABELS,
  createMission,
  deleteMission,
  listMissionSummaries,
} from '@signals/app-shell/services/missionApi';
import MissionWorkspace from '@signals/app-shell/features/missions/MissionWorkspace';
import { metalMenuItem, metalMenuPaper, metalSx } from '@signals/app-shell/theme/metalButtons';
import IntelPacketWorkspace from './intel/IntelPacketWorkspace';
import CreatePacketDialog from './intel/CreatePacketDialog';

const toolBtn = {
  height: 32,
  minHeight: 32,
  minWidth: 32,
  width: 32,
  px: 0,
  textTransform: 'none' as const,
};

export const INTEL_TAG = 'intel';

/** A packet and a mission are both files you open, so the list holds either. */
export type FileKind = 'packet' | 'mission';
export interface FileSelection {
  kind: FileKind;
  id: number;
}

interface FileRow {
  kind: FileKind;
  id: number;
  name: string;
  createdAt: string;
  subtitle: string;
  detail: string;
  statusLabel: string;
  statusColor: string;
  searchText: string;
}

interface IntelPacketPanelProps {
  intelEnabled: boolean;
  selected?: FileSelection | null;
  onSelect?: (selection: FileSelection | null) => void;
  onZoomTo?: (lat: number, lng: number, zoom?: number) => void;
  refreshSignal?: number;
  onLoadPacketLayer?: (
    layerId: string,
    points: Array<{ latitude: number; longitude: number; label: string; entityId?: string; visits?: number; duration?: number }>,
    line?: GeoJSON.LineString | null,
  ) => void;
  onLoadPacketGeofences?: (packetId: number, fences: PacketMapPolygon[]) => void;
  onClearPacketLayers?: (packetId: number) => void;
  /** Draw the selected mission's route and stops on the host map. */
  onShowMission?: (mission: Mission | null) => void;
  /**
   * Bumped when the host has changed the open mission itself, as it can from
   * the reference dock. Distinct from `refreshSignal`, which reloads the file
   * list — a reroute changes one mission, not which files exist.
   */
  missionSignal?: number;
  onFocusPoint?: (longitude: number, latitude: number) => void;
  liveStatus?: MissionRouteStatus | null;
  onLiveStatus?: (status: MissionRouteStatus) => void;
  onWaypointPickChange?: (active: boolean) => void;
  waypointPickRef?: React.MutableRefObject<((longitude: number, latitude: number) => void) | null>;
  onEmergencyReroute?: () => void;
  onCancelEmergencyReroute?: () => void;
  emergencyReroutePicking?: boolean;
  emergencyRerouteBusy?: boolean;
  emergencyRerouteError?: string | null;
  onOpenPhotos?: () => void;
}

function packetRow(p: IntelPacket): FileRow {
  const status = normalizePacketStatus(p.status);
  return {
    kind: 'packet',
    id: p.id,
    name: p.name,
    createdAt: p.created_at,
    subtitle: `${p.primary_subject_type ? p.primary_subject_type.replace(/_/g, ' ') : 'unscoped'}`
      + (p.last_meaningful_at
        ? ` · ${new Date(p.last_meaningful_at).toLocaleString()}`
        : ' · waiting on first take'),
    detail: formatPacketMonitorLine(p),
    statusLabel: PACKET_STATUS_LABELS[status],
    statusColor: PACKET_STATUS_COLOR[status],
    searchText: [p.name, p.status, p.primary_subject_type, p.assigned_analyst]
      .filter(Boolean).join(' ').toLowerCase(),
  };
}

function missionRow(m: MissionSummary): FileRow {
  const phones = m.assignee_count;
  const stops = m.waypoint_count;
  const parts = [
    m.packet_name ? `filed under ${m.packet_name}` : 'standalone',
    `${stops} stop${stops === 1 ? '' : 's'}`,
  ];
  if (phones) parts.push(`${phones} phone${phones === 1 ? '' : 's'}`);
  if (m.route_stats?.distance != null) parts.push(`${(m.route_stats.distance / 1000).toFixed(1)} km`);
  return {
    kind: 'mission',
    id: m.id,
    name: m.name,
    createdAt: m.created_at,
    subtitle: `mission · ${parts[0]}`,
    detail: parts.slice(1).join(' · ') || 'no route planned',
    statusLabel: MISSION_STATUS_LABELS[m.status],
    statusColor: MISSION_STATUS_COLOR[m.status],
    searchText: [m.name, 'mission', m.status, m.objective, m.packet_name]
      .filter(Boolean).join(' ').toLowerCase(),
  };
}

const IntelPacketPanel: React.FC<IntelPacketPanelProps> = ({
  intelEnabled,
  selected: selectedProp,
  onSelect,
  onZoomTo,
  refreshSignal,
  onLoadPacketLayer,
  onLoadPacketGeofences,
  onClearPacketLayers,
  onShowMission,
  missionSignal,
  onFocusPoint,
  liveStatus,
  onLiveStatus,
  onWaypointPickChange,
  waypointPickRef,
  onEmergencyReroute,
  onCancelEmergencyReroute,
  emergencyReroutePicking,
  emergencyRerouteBusy,
  emergencyRerouteError,
  onOpenPhotos,
}) => {
  const [packets, setPackets] = useState<IntelPacket[]>([]);
  const [missions, setMissions] = useState<MissionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocal, setSelectedLocal] = useState<FileSelection | null>(null);
  const selected = onSelect ? (selectedProp ?? null) : selectedLocal;
  const setSelected = onSelect || setSelectedLocal;
  const [createOpen, setCreateOpen] = useState(false);
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const [missionOpen, setMissionOpen] = useState(false);
  const [missionName, setMissionName] = useState('');
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | FileKind>('all');

  const load = useCallback(async () => {
    if (!intelEnabled) return;
    setLoading(true);
    setError(null);
    try {
      const [nextPackets, nextMissions] = await Promise.all([
        listPackets(),
        listMissionSummaries().catch(() => [] as MissionSummary[]),
      ]);
      setPackets(nextPackets);
      setMissions(nextMissions);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [intelEnabled]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  useEffect(() => {
    if (!intelEnabled) return;
    const handler = () => { load(); };
    window.addEventListener('refresh-intel-packet', handler);
    return () => window.removeEventListener('refresh-intel-packet', handler);
  }, [intelEnabled, load]);

  const rows = useMemo(() => {
    const all = [...packets.map(packetRow), ...missions.map(missionRow)];
    // Newest first, so the thing you just made is the thing you see.
    return all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [packets, missions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => (kindFilter === 'all' || r.kind === kindFilter)
      && (!q || r.searchText.includes(q)));
  }, [rows, query, kindFilter]);

  // The workspace below owns what the map draws while a mission is open: it
  // holds the full mission and pushes it through `onWaypointsChange`. This only
  // has to clear the map when the mission closes, since at that point the
  // workspace has unmounted and nothing else will.
  const missionOpenId = selected?.kind === 'mission' ? selected.id : null;
  useEffect(() => {
    if (missionOpenId === null) onShowMission?.(null);
  }, [missionOpenId, onShowMission]);

  const submitMission = useCallback(async () => {
    const name = missionName.trim();
    if (!name) return;
    try {
      const created = await createMission({ name });
      setMissionOpen(false);
      await load();
      setSelected({ kind: 'mission', id: created.id });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not create the mission');
    }
  }, [missionName, load, setSelected]);

  if (!intelEnabled) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Intel Packet</Typography>
        <Typography variant="caption" color="text.secondary">
          Enable it in Settings → Modules to open case files on a geofence or device.
        </Typography>
      </Box>
    );
  }

  if (selected?.kind === 'packet') {
    return (
      <IntelPacketWorkspace
        packetId={selected.id}
        onBack={() => { onClearPacketLayers?.(selected.id); setSelected(null); load(); }}
        onZoomTo={onZoomTo}
        onLoadPacketLayer={onLoadPacketLayer}
        onLoadPacketGeofences={onLoadPacketGeofences}
        onClearPacketLayers={onClearPacketLayers}
        onWaypointPickChange={onWaypointPickChange}
        waypointPickRef={waypointPickRef}
      />
    );
  }

  if (selected?.kind === 'mission') {
    return (
      <Box sx={{ height: '100%', overflow: 'auto', px: 1.75, py: 1.5 }}>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
        <MissionWorkspace
          missionId={selected.id}
          onBack={() => { setSelected(null); load(); }}
          onError={setError}
          onFocusPoint={onFocusPoint}
          onWaypointsChange={onShowMission}
          refreshSignal={missionSignal}
          // IntelPacketPage puts the run in the reference dock on the far side
          // of the map. Keeping it here as well would poll twice and show two
          // of every alert.
          hideRunStatus
          liveStatus={liveStatus}
          onLiveStatus={onLiveStatus}
          onWaypointPickChange={onWaypointPickChange}
          waypointPickRef={waypointPickRef}
          onEmergencyReroute={onEmergencyReroute}
          onCancelEmergencyReroute={onCancelEmergencyReroute}
          emergencyReroutePicking={emergencyReroutePicking}
          emergencyRerouteBusy={emergencyRerouteBusy}
          emergencyRerouteError={emergencyRerouteError}
          onOpenPhotos={onOpenPhotos}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1,
              bgcolor: 'rgba(245,185,66,0.16)', color: '#f5b942',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FactCheckIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.2, lineHeight: 1.15 }}>
                Intel
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {packets.length} packet{packets.length === 1 ? '' : 's'}
                {' · '}
                {missions.length} mission{missions.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title="Refresh">
              <Button
                aria-label="Refresh"
                onClick={load}
                sx={{ ...toolBtn, ...metalSx('dlc') }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
            <Tooltip title="New file">
              <Button
                aria-label="New file"
                onClick={(e) => setCreateAnchor(e.currentTarget)}
                sx={{ ...toolBtn, ...metalSx('steel') }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          </Box>
        </Box>
        <TextField
          size="small"
          fullWidth
          placeholder="Search packets and missions"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
          {(['all', 'packet', 'mission'] as const).map((k) => (
            <Button
              key={k}
              size="small"
              onClick={() => setKindFilter(k)}
              sx={{
                ...metalSx(kindFilter === k ? 'steel' : 'gun'),
                height: 26,
                minHeight: 26,
                px: 1.15,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.68rem',
                letterSpacing: 0.2,
              }}
            >
              {k === 'all' ? 'All' : k === 'packet' ? 'Packets' : 'Missions'}
            </Button>
          ))}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.25 }}>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{
            mt: 1, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider',
            textAlign: 'center',
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {rows.length === 0 ? 'Nothing on file yet' : 'No matches'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Open a packet on a geofence or device, or plan a mission to send phones out.
            </Typography>
            <Button
              size="small"
              onClick={() => setCreateOpen(true)}
              sx={{
                ...metalSx('steel'),
                height: 26,
                px: 1.25,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.72rem',
              }}
            >
              New packet
            </Button>
          </Box>
        ) : (
          filtered.map((row) => (
            <Box
              key={`${row.kind}-${row.id}`}
              onClick={() => setSelected({ kind: row.kind, id: row.id })}
              sx={{
                p: 1.25,
                mb: 1,
                borderRadius: 1.5,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                // A mission reads as a route, so it gets a rail in its status
                // colour rather than another badge competing with the chip.
                borderLeft: row.kind === 'mission' ? `3px solid ${row.statusColor}` : '1px solid',
                borderLeftColor: row.kind === 'mission' ? row.statusColor : 'divider',
                transition: 'border-color 120ms, transform 120ms',
                '&:hover': {
                  borderColor: 'rgba(245,185,66,0.55)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{row.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                    {row.subtitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.detail}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={row.statusLabel}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: `${row.statusColor}22`,
                    color: row.statusColor,
                    border: `1px solid ${row.statusColor}55`,
                  }}
                />
                <Tooltip title={row.kind === 'mission' ? 'Delete mission' : 'Delete packet'}>
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete ${row.name}?`)) return;
                      if (row.kind === 'mission') await deleteMission(row.id);
                      else await deletePacket(row.id);
                      if (selected?.kind === row.kind && selected.id === row.id) setSelected(null);
                      load();
                    }}
                    sx={{ mt: -0.4 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Menu
        anchorEl={createAnchor}
        open={!!createAnchor}
        onClose={() => setCreateAnchor(null)}
        slotProps={{ paper: { sx: metalMenuPaper } }}
      >
        <MenuItem
          dense
          onClick={() => { setCreateAnchor(null); setCreateOpen(true); }}
          sx={metalMenuItem}
        >
          New packet
        </MenuItem>
        <MenuItem
          dense
          onClick={() => { setCreateAnchor(null); setMissionName(''); setMissionOpen(true); }}
          sx={metalMenuItem}
        >
          New mission
        </MenuItem>
      </Menu>

      <CreatePacketDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          load();
          setSelected({ kind: 'packet', id: created.id });
        }}
      />

      <Dialog open={missionOpen} onClose={() => setMissionOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>New mission</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            A field task with a planned route and assigned phones. Attach it to a packet later
            if it needs a case file.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Mission name"
            value={missionName}
            onChange={(e) => setMissionName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && missionName.trim()) submitMission(); }}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setMissionOpen(false)}>Cancel</Button>
            <Button size="small" variant="contained" disabled={!missionName.trim()} onClick={submitMission}>
              Create
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IntelPacketPanel;
